import { ReactNode, useMemo } from 'react';
import { HOME_V2_COLORS } from './colors.js';
import { WRAPPER_GRADIENT } from './timeOfDayConfig.js';
import type { TimeOfDayConfig } from './timeOfDayConfig.js';
import NightSkyOverlay from './NightSkyOverlay.js';
//import MorningGlowOverlay from './MorningGlowOverlay.js';

interface HomeV2BackgroundProps {
  children?: ReactNode;
  /** Time-of-day config for dynamic gradient. Falls back to default if not provided. */
  timeOfDayConfig?: TimeOfDayConfig;
  /** Custom CSS gradient string that completely overrides the default gradient layers. */
  backgroundGradient?: string;
  /** Whether to show night sky overlay */
  showNightDecorations?: boolean;
  /** Whether to show morning glow overlay */
  showMorningGlow?: boolean;
  /** Whether the check-in transition is active (fades out the per-period BG overlay) */
  isTransitioning?: boolean;

  /** the function to use to generate custom background gradient */
  gradientFunction?: BackgroundFunctionKey;
}

export function buildLibraryGradient(config?: TimeOfDayConfig): string {
  const topColor = config?.headerBackground ?? '#01A7C8';
  return `linear-gradient(180deg, ${topColor} 0%, ${topColor} 195px, #FFFFFF 534px, #FFFFFF 100%)`;
}

/**
 * Builds the profile page gradient.
 *
 * The profile layout is a self-contained single gradient (not the two-layer home system):
 * - Solid header zone (0–140px) using the per-period header color
 * - Smooth fade to the content area background (#F5F7FA) by 534px
 * - Stays at the content color below that
 *
 * Uses config.headerBackground (the solid per-period color) as the top color.
 * Falls back to #01A7C8 (default teal) when no config is provided.
 */
export function buildProfileGradient(config?: TimeOfDayConfig, overrideStaticBackground?: string): string {
  // Custom gradient for profile screen — solid teal until ~195px, smooth fade to page bg by 534px (matches Figma BG node)
  //const profileGradient = `linear-gradient(180deg, #01A7C8 0%, #01A7C8 140px, #F5F7FA 534px, #F5F7FA 100%)`;

  if (typeof overrideStaticBackground === 'string') {
    return overrideStaticBackground
  }
 
  const topColor = config?.headerBackground ?? '#01A7C8';
  return `linear-gradient(180deg, ${topColor} 0%, ${topColor} 140px, #F5F7FA 534px, #F5F7FA 100%)`;
}

function buildBgGradient(config?: TimeOfDayConfig): string {
  if (!config) {
    // Fallback: original behavior (single gradient)
    return `linear-gradient(to bottom, ${HOME_V2_COLORS.gradientTop} 0px, ${HOME_V2_COLORS.gradientTop} 197px, ${HOME_V2_COLORS.gradientBottom} 859px, ${HOME_V2_COLORS.gradientBottom} 100%)`;
  }

  // BG gradient (per-period) — upper layer
  if (config.bgGradientVia) {
    return `linear-gradient(to bottom, ${config.bgGradientStart} 0%, ${config.bgGradientVia} ${config.bgGradientViaPosition}, ${config.bgGradientEnd} ${config.bgGradientEndPosition})`;
  }
  return `linear-gradient(to bottom, ${config.bgGradientStart} 0%, ${config.bgGradientEnd} ${config.bgGradientEndPosition})`;
}

function buildExpertTipsBgGradient(config?: TimeOfDayConfig): string {
  if (!config) {
    // Fallback: original behavior (single gradient)
    return `linear-gradient(to bottom, #01A7C8 0%, #01A7C8 20%, #FFFFFF 50%, #FFFFFF 100%)`;
  }

  // BG gradient (per-period) — upper layer
  if (config.bgGradientVia) {
    //linear-gradient(to bottom, #01A7C8 0%, #01A7C8 20%, #FFFFFF 50%, #FFFFFF 100%)
    return `linear-gradient(to bottom, ${config.bgGradientStart} 0%, ${config.bgGradientVia} 20%, ${config.bgGradientEnd} 50%, ${config.bgGradientEnd} 100%)`;
  }
  //return `linear-gradient(to bottom, ${config.bgGradientStart} 0%, ${config.bgGradientStart} 20%, ${config.bgGradientEnd} 50%, ${config.bgGradientEnd} 100%)`;
  return `linear-gradient(to bottom, ${config.bgGradientStart} 0%, ${config.bgGradientEnd} ${config.bgGradientEndPosition})`;
}

const backgroundFunctionMap = {
  'expert-tips': buildExpertTipsBgGradient,
  'default': buildBgGradient,
}

export type BackgroundFunctionKey = keyof typeof backgroundFunctionMap;


/**
 * HomeV2Background Component
 *
 * Scrollable background container with vertical gradient.
 * Supports time-of-day dynamic gradients with a two-layer approach:
 * 1. BG gradient (per-period, top ~500px)
 * 2. Wrapper gradient (consistent cyan → white, full height)
 *
 * Night sky overlay (stars, glow) renders between the two layers.
 */
export default function HomeV2Background({ 
  children, 
  timeOfDayConfig, 
  backgroundGradient, 
  showNightDecorations, 
  isTransitioning = false,
  gradientFunction = 'default',
}: HomeV2BackgroundProps) {
  
  const generatedBackgroundGradient = useMemo(() => {

    if (!timeOfDayConfig) {
      return (backgroundFunctionMap[gradientFunction] ?? backgroundFunctionMap.default)();
    } else {
      return (backgroundFunctionMap[gradientFunction] ?? backgroundFunctionMap.default)(timeOfDayConfig);
    }

  }, [gradientFunction, timeOfDayConfig])


  if (backgroundGradient) {
    // Custom gradient override — single gradient, no layers
    return (
      <div
        className="min-h-screen"
        style={{
          background: backgroundGradient,
          overflowY: 'auto',
          scrollbarGutter: 'stable both-edges',
        }}
      >
        {children}
      </div>
    );
  }

  if (!timeOfDayConfig) {
    // Original behavior — single gradient, no layers
    return (
      <div
        className="min-h-screen"
        style={{
          background:generatedBackgroundGradient,
          overflowY: 'auto',
          scrollbarGutter: 'stable both-edges',
        }}
      >
        {children}
      </div>
    );
  }

  const bgGradient = generatedBackgroundGradient;
  const wrapperGradient = `linear-gradient(to bottom, ${WRAPPER_GRADIENT.from} ${WRAPPER_GRADIENT.fromPosition}, ${WRAPPER_GRADIENT.to} ${WRAPPER_GRADIENT.toPosition})`;

  return (
    <div className="min-h-screen relative" 
    style={{ 
      overflowY: 'auto', 
      //scrollbarGutter: 'stable both-edges', 
      background: wrapperGradient 
      }}>
      {/* BG gradient layer (per-period, top portion) */}
      <div
        className="absolute inset-x-0 top-0 pointer-events-none"
        style={{
          background: bgGradient,
          height: '500px',
          transition: isTransitioning
            ? 'opacity 0.4s ease-in-out'
            : 'background 0.5s ease-in-out',
          opacity: isTransitioning ? 0 : 1,
        }}
      />

      {/* Morning glow overlay (warm sunrise radials) — only during morning period */}
      {/* {showMorningGlow && <MorningGlowOverlay />} */}

      {/* Night sky overlay (stars, glow) — only during night period */}
      {showNightDecorations && <NightSkyOverlay />}

      {/* Content layer (transparent, stacks above gradients) */}
      <div
        className="relative min-h-screen"
      >
        {children}
      </div>
    </div>
  );
}
